import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Underline } from '@tiptap/extension-underline';
import { BulletList } from '@tiptap/extension-bullet-list';
import { HardBreak } from '@tiptap/extension-hard-break';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { History } from '@tiptap/extension-history';
import { ListItem } from '@tiptap/extension-list-item';
import { Heading } from '@tiptap/extension-heading';
import { Markdown } from '@tiptap/markdown';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { SearchAndReplace } from '../../shared/extensions/search-and-replace';
import { AiChangeDecoration } from './ai-change-decoration.extension';

export const EDITOR_HISTORY_OPTIONS = { depth: 100, newGroupDelay: 500 };

/** Plain-text copy. The core clipboard serializer and `editor.getText()` both use this. */
export const CLIPBOARD_BLOCK_SEPARATOR = '\n';

function isSpacerText(text: string): boolean {
  return text.replace(/[\s\u200e\u00a0]/g, '') === '';
}

/** Absolute document positions, as passed by the clipboard serializer. */
type Range = { from: number; to: number };

const WHOLE_NODE: Range = { from: 0, to: Infinity };

/** `renderText` does not declare `range`; the clipboard serializer passes it at runtime. */
type PlainTextProps = {
  node: ProseMirrorNode;
  pos: number;
  parent: ProseMirrorNode;
  index: number;
  range?: Range;
};

/**
 * Selected text of the textblock at `pos`, with hard breaks as newlines.
 * `null` when none of it is selected; `''` for a spacer (blank lines in a letter are
 * paragraphs of a left-to-right mark and a space).
 */
function textblockText(node: ProseMirrorNode, pos: number, range: Range): string | null {
  const contentStart = pos + 1;
  const from = Math.max(0, range.from - contentStart);
  const to = Math.min(node.content.size, range.to - contentStart);
  if (from >= to) return null;
  if (isSpacerText(node.textContent)) return '';
  return node.textBetween(from, to, '', leaf => (leaf.type.name === 'hardBreak' ? '\n' : ''));
}

const renderParagraphText = ({ node, pos, range = WHOLE_NODE }: PlainTextProps): string =>
  textblockText(node, pos, range) ?? '';

function listMarker(list: ProseMirrorNode, depth: number, index: number): string {
  if (list.type.name === 'orderedList') {
    const start = Number(list.attrs['start'] ?? 1);
    return `${start + index}. `;
  }
  return depth === 0 ? '• ' : '◦ ';
}

function isList(node: ProseMirrorNode): boolean {
  return node.type.name === 'bulletList' || node.type.name === 'orderedList';
}

/** The first selected line of an item carries its marker; later lines align under its text. */
function listLines(list: ProseMirrorNode, pos: number, depth: number, range: Range): string[] {
  const indent = '  '.repeat(depth);
  const lines: string[] = [];
  list.forEach((item, itemOffset, index) => {
    const itemPos = pos + 1 + itemOffset;
    const marker = listMarker(list, depth, index);
    const continuation = indent + ' '.repeat(marker.length);
    let prefix = indent + marker;
    item.forEach((child, childOffset) => {
      const childPos = itemPos + 1 + childOffset;
      if (isList(child)) {
        lines.push(...listLines(child, childPos, depth + 1, range));
        return;
      }
      const text = child.isTextblock ? textblockText(child, childPos, range) : null;
      if (text === null) return;
      if (text) {
        text.split('\n').forEach((line, lineIndex) => lines.push((lineIndex ? continuation : prefix) + line));
      } else {
        lines.push(prefix.trimEnd());
      }
      prefix = continuation;
    });
  });
  return lines;
}

/** Serialize the list as one block so each wrapper does not add its own line break. */
const renderListText = ({ node, pos, range = WHOLE_NODE }: PlainTextProps): string =>
  listLines(node, pos, 0, range).join('\n');

const ParagraphWithPlainText = Paragraph.extend({
  renderText: renderParagraphText,
});

const BulletListWithPlainText = BulletList.extend({
  renderText: renderListText,
});

const orderedListTokenizer = OrderedList.config.markdownTokenizer;

/** `Ms.` is not a list marker. `\.` parses back to `.`. */
const OrderedListWithoutLetterMarkers = OrderedList.extend({
  renderText: renderListText,
  markdownTokenizer: {
    name: 'orderedList',
    level: 'block',
    start: () => -1,
    tokenize(src, tokens, lexer) {
      const escaped = src.replace(/^([ \t]*)([A-Za-z]{1,2})([.)])(?=[ \t])/gm, '$1$2\\$3');
      const token = orderedListTokenizer?.tokenize(escaped, tokens, lexer);
      if (!token?.raw) {
        return token;
      }
      const consumedLines = token.raw.split('\n').length;
      return {
        ...token,
        raw: src.split('\n').slice(0, consumedLines).join('\n'),
      };
    },
  },
});

const BoldAsB = Bold.configure({
  HTMLAttributes: {
    class: null,
  },
}).extend({
  parseHTML() {
    return [
      { tag: 'b' },
      { tag: 'strong', getAttrs: () => ({ convertToB: true }) },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['b', HTMLAttributes, 0];
  },
});

const ItalicAsI = Italic.configure({
  HTMLAttributes: {
    class: null,
  },
}).extend({
  parseHTML() {
    return [
      { tag: 'i' },
      { tag: 'em', getAttrs: () => ({ convertToI: true }) },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['i', HTMLAttributes, 0];
  },
});

const UnderlineAsU = Underline.configure({
  HTMLAttributes: {
    class: null,
  },
}).extend({
  parseHTML() {
    return [
      { tag: 'u' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['u', HTMLAttributes, 0];
  },
});

const MAX_HEADING_LEVEL = 3;

const HeadingUpToH3 = Heading.extend({
  parseMarkdown: (token, helpers) =>
    helpers.createNode(
      'heading',
      { level: Math.min(token.depth || 1, MAX_HEADING_LEVEL) },
      helpers.parseInline(token.tokens || []),
    ),
}).configure({ levels: [1, 2, 3] });

const ListItemWithoutCheckbox = ListItem.extend({
  parseMarkdown: (token, helpers) =>
    ListItem.config.parseMarkdown!(
      { ...token, tokens: token.tokens?.filter(child => child.type !== 'checkbox') },
      helpers,
    ),
});

/**
 * Schema-relevant extensions shared by the live editor and the Markdown parser.
 * Keep these in one place so headings/lists/marks cannot drift between the two.
 */
export function createNoteContentExtensions() {
  return [
    Document,
    Text,
    ParagraphWithPlainText,
    BoldAsB,
    ItalicAsI,
    UnderlineAsU,
    HardBreak,
    ListItemWithoutCheckbox,
    BulletListWithPlainText,
    OrderedListWithoutLetterMarkers,
    HeadingUpToH3,
    Markdown,
  ];
}

export function createEditorExtensions() {
  return [
    ...createNoteContentExtensions(),
    TextStyle,
    Color,
    History.configure(EDITOR_HISTORY_OPTIONS),
    SearchAndReplace.configure({ searchResultClass: 'ai-search-highlight', disableRegex: true }),
    AiChangeDecoration,
  ];
}
